import { ls } from '@iqueue/ui-kit'
import usePermissions from '../components/usePermissions'
import useTranslate, { t } from './useTranslate'
import { useMemo } from 'react'
import { omit } from 'ramda'

function removeDuplicates(array) {
  const seenRoutes = new Set()
  return array.filter(item => {
    if (seenRoutes.has(item.route)) {
      return false
    }
    seenRoutes.add(item.route)
    return true
  })
}

function filteredTabs(items, permissions) {
  const routeConditions = [
    {
      condition: () => permissions.NODE?.includes('READ'),
      routes: ['map', 'production'],
    },
    {
      condition: () => permissions.EVENT?.includes('READ'),
      routes: ['logs', 'notices', 'statistics'],
    },
    {
      condition: () => permissions.SETTINGS?.includes('READ'),
      routes: ['settings'],
    },
    {
      condition: () =>
        permissions.PROFILE?.includes('READ') &&
        permissions.NODE?.includes('READ') &&
        permissions.SENSOR?.includes('READ') &&
        permissions.RESPONSIBILITY?.includes('READ'),
      routes: ['responsibilities'],
      filterOut: true,
    },
    {
      condition: () =>
        permissions.SENSOR?.includes('READ') &&
        permissions.VERIFICATION?.includes('READ'),
      routes: ['verifications'],
    },
  ]

  let tabsObject = []

  routeConditions.forEach(({ condition, routes, filterOut }) => {
    if (condition()) {
      routes.forEach(route => {
        items.some(item => item.route === route && tabsObject.push(item))
      })
    } else if (filterOut) {
      tabsObject = tabsObject.filter(item => !routes.includes(item.route))
    }
  })

  items.forEach(item => {
    item.pageRoutes.forEach(route => {
      if (permissions[route]?.includes('READ')) {
        tabsObject.push(item)
      }
    })
  })

  const uniqueTabs = removeDuplicates(tabsObject)
  const sortOrder = [
    'PRODUCTION',
    'MAP',
    'MODEL',
    'LOGS',
    'UPLOAD',
    'PROFILE',
    'NODES',
    'SENSORS',
    'USERS',
    'STATISTICS',
    'NOTICES',
    'INSPECTION-LOGS',
    'OPTIONS',
    'responsibilities',
    'RESPONSIBILITIES',
    'ROLES',
    'VERIFICATIONS',
    'SETTINGS',
    'LICENSE',
  ]

  return uniqueTabs.sort((a, b) => {
    const indexA = sortOrder.indexOf(a.route.toUpperCase())
    const indexB = sortOrder.indexOf(b.route.toUpperCase())

    if (indexA === -1) return 1
    if (indexB === -1) return -1

    return indexA - indexB
  })
}

function getNavItems(perms) {
  const token = ls('amber-auth-token')
  const permissions = perms || ls('permissions')
  if (!token || !permissions) {
    return [{
      id: 5,
      title: 'Auth',
      route: 'auth-form',
      icon: 'person',
    }]
  }

  if (!permissions.STATE && permissions.NODE?.includes('READ')) {
    permissions.STATE = ['READ']
  }

  const items = [{
    id: 1,
    title: t('menu.production'),
    route: 'production',
    icon: 'remove_red_eye',
    pageRoutes: ['NODE'],
  }, {
    id: 2,
    title: t('menu.map'),
    route: 'map',
    icon: 'map',
    pageRoutes: ['MAP'],
  }, {
    id: 4,
    title: t('menu.model'),
    route: 'model',
    icon: 'chrome_reader_mode',
    pageRoutes: ['MODEL', 'FABRICATOR'],
  }, {
    id: 3,
    title: t('menu.logs'),
    route: 'logs',
    icon: 'delete',
    // }, {
    //   id: 4,
    //   title: 'Hierarchy',
    //   route: 'hierarchy',
    //   icon: 'account_tree',
    pageRoutes: ['log'],
  },
    // {
    //   id: 7,
    //   title: t('menu.profile'),
    //   route: 'user-integration',
    //   icon: 'person',
    //   pageRoutes: ['PROFILE'],
    // },
    {
      id: 8,
      title: t('menu.licenses'),
      route: 'license',
      icon: 'publish',
      pageRoutes: ['LICENSE'],
    },
    {
      id: 9,
      title: t('menu.settings'),
      route: 'settings',
      icon: 'settings',
      pageRoutes: ['SETTINGS'],
    },
    {
      id: 10,
      title: t('menu.options'),
      route: 'options',
      icon: 'settings',
      pageRoutes: ['OPTION'],
    },
    {
      id: 11,
      title: t('menu.nodes'),
      route: 'nodes',
      icon: 'device_hub',
      pageRoutes: ['NODE'],
    },
    {
      id: 12,
      title: t('menu.sensors'),
      route: 'sensors',
      icon: 'sensors',
      pageRoutes: ['SENSOR'],
    },
    {
      id: 13,
      title: t('menu.users'),
      route: 'users',
      icon: 'group',
      pageRoutes: ['USER'],
    },
    {
      id: 14,
      title: t('menu.reports'),
      route: 'statistics',
      icon: 'download',
      pageRoutes: ['STATISTIC'],
    },
    {
      id: 15,
      title: t('menu.notices'),
      route: 'notices',
      icon: 'notifications',
      pageRoutes: ['NOTICES'],
    },
    {
      id: 16,
      title: t('menu.inspectionLog'),
      route: 'inspection-logs',
      icon: 'family_history',
      pageRoutes: ['INSPECTION_LOG'],
    },
    {
      id: 17,
      title: t('menu.responsibility'),
      route: 'responsibilities',
      icon: 'visibility',
      pageRoutes: ['RESPONSIBILITY'],
    },
    {
      id: 18,
      title: t('menu.roles'),
      route: 'roles',
      icon: 'assignment_ind',
      pageRoutes: ['ROLE'],
    },
    {
      id: 19,
      title: t('menu.verification'),
      route: 'verifications',
      icon: 'new_releases',
      pageRoutes: ['VERIFICATION'],
    },
    {
      id: 20,
      title: t('menu.state'),
      route: 'state',
      icon: 'visibility',
      pageRoutes: ['STATE'],
    },
    {
      id: 21,
      title: t('menu.tuner'),
      route: 'tuner',
      icon: 'memory',
      // pageRoutes: ['TUNER'],
      pageRoutes: ['SETTINGS'],
    },
  ]

  const isDev = process.env.NODE_ENV === 'development'

  if (isDev) {
    items.forEach(item => {
      if (['settings', 'model'].includes(item.route)) {
        item.style = {
          background: 'var(--iq-warning)',
        }
      }
    })
  }

  const filteredItems = filteredTabs(items, permissions)
  if (!filteredItems.length) {
    return [{
      id: 5,
      title: 'No Access',
      route: 'no-permissions',
      icon: 'person_off',
    }]
  }

  return filteredItems
}

export default function useNav() {
  const { permissions } = usePermissions()
  const locale = useTranslate()

  return useMemo(() => {
    return getNavItems(permissions, t).map(omit(['pageRoutes']))
  }, [permissions, t, locale])
}
